import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRingCentralSafe } from '../../providers/RingCentralProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { useVonageSafe } from '../../providers/VonageProvider';
import { LoadingSpinner } from '../display/LoadingSpinner';
import { OAuthHandlerRegistry } from './handlers/OAuthHandlerRegistry';
import { QboOAuthHandler } from './handlers/QboOAuthHandler';
import { RingCentralOAuthHandler } from './handlers/RingCentralOAuthHandler';
import { VonageOAuthHandler } from './handlers/VonageOAuthHandler';
import type { OAuthCallbackParams } from './oauth-callback.types';

OAuthHandlerRegistry.register(RingCentralOAuthHandler);
OAuthHandlerRegistry.register(QboOAuthHandler);
OAuthHandlerRegistry.register(VonageOAuthHandler);

type CallbackStatus = 'processing' | 'success' | 'error';

export function OAuthCallbackPage(): React.ReactElement {
	const { t } = useTranslation();
	const ringCentral = useRingCentralSafe();
	const vonage = useVonageSafe();
	const [status, setStatus] = useState<CallbackStatus>('processing');
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const processedRef = useRef(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (processedRef.current) {
			return;
		}

		processedRef.current = true;

		const handleCallback = async (): Promise<void> => {
			const urlParams = new URLSearchParams(window.location.search);

			const params: OAuthCallbackParams = {
				code: urlParams.get('code'),
				state: urlParams.get('state'),
				error: urlParams.get('error'),
				errorDescription: urlParams.get('error_description'),
				realmId: urlParams.get('realmId'),
			};

			if (params.error != null) {
				setStatus('error');
				setErrorMessage(params.errorDescription ?? params.error);

				return;
			}

			if (params.code == null || params.state == null) {
				setStatus('error');
				setErrorMessage(
					t('Invalid OAuth callback. Missing required parameters.'),
				);

				return;
			}

			const handler = OAuthHandlerRegistry.findHandler(params);

			if (handler == null) {
				setStatus('error');
				setErrorMessage(
					t('Unknown OAuth callback. Unable to determine integration.'),
				);

				return;
			}

			window.history.replaceState({}, document.title, window.location.pathname);

			try {
				const result = await handler.handle(params);

				if (result.success) {
					setStatus('success');

					if (result.closePopup === true) {
						const opener = window.opener as Window | null;

						if (opener != null && result.postMessage != null) {
							opener.postMessage(result.postMessage, window.location.origin);
						}

						window.close();

						return;
					}

					// Refresh provider status based on which callback handler was used
					if (handler.name === 'ringcentral') {
						void ringCentral?.refreshStatus();
					} else if (handler.name === 'vonage') {
						void vonage?.refreshStatus();
					}

					if (result.redirectTo != null) {
						void navigate(result.redirectTo);
					}
				} else {
					// For user flows (closePopup=true), send error to opener and close
					if (result.closePopup === true) {
						const opener = window.opener as Window | null;

						if (opener != null && result.postMessage != null) {
							opener.postMessage(result.postMessage, window.location.origin);
						}

						window.close();

						return;
					}

					// For admin flows, show error UI
					setStatus('error');
					setErrorMessage(
						result.error ?? t('Failed to complete authentication'),
					);
				}
			} catch (err) {
				console.error('Error handling OAuth callback:', err);
				setStatus('error');
				setErrorMessage(
					t('An unexpected error occurred during authentication'),
				);
			}
		};

		void handleCallback();
	}, [navigate, ringCentral, vonage, t]);

	if (status === 'processing') {
		return (
			<div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
				<div className="text-center space-y-4">
					<LoadingSpinner size="lg" color="primary" />
					<p className="text-gray-600 dark:text-gray-400">
						{t('Processing authentication...')}
					</p>
				</div>
			</div>
		);
	}

	if (status === 'error') {
		return (
			<div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
				<div className="text-center space-y-4 max-w-md px-4">
					<div className="text-red-500 text-5xl">!</div>
					<h1 className="text-xl font-semibold text-gray-900 dark:text-white">
						{t('Authentication Failed')}
					</h1>
					<p className="text-gray-600 dark:text-gray-400">
						{t(errorMessage ?? 'An error occurred during authentication')}
					</p>
					<button
						type="button"
						onClick={() => {
							void navigate('/settings/integrations');
						}}
						className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
					>
						{t('Return to Integrations')}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
			<div className="text-center space-y-4">
				<div className="text-green-500 text-5xl">✓</div>
				<p className="text-gray-600 dark:text-gray-400">
					{t('Authentication successful! Redirecting...')}
				</p>
			</div>
		</div>
	);
}
